import { createContext, useContext, useEffect, useState } from "react";
import { BaseStyles } from "@primer/react";
import { ThemeProvider } from "@primer/react/next";
import { useFocusVisibility } from "./useFocusVisibility.js";

const themes = ["system", "light", "dark"];
const storageKey = "regulator:theme";
const AppThemeContext = createContext(null);

function initialTheme() {
  try {
    const storedTheme = window.localStorage?.getItem(storageKey);
    return themes.includes(storedTheme) ? storedTheme : "system";
  } catch {
    return "system";
  }
}

function systemTheme() {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function AppTheme({ children }) {
  useFocusVisibility();
  const [appearance, setAppearance] = useState(initialTheme);
  const [systemAppearance, setSystemAppearance] = useState(systemTheme);

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) return undefined;
    const change = () => setSystemAppearance(media.matches ? "dark" : "light");
    media.addEventListener?.("change", change);
    return () => media.removeEventListener?.("change", change);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = appearance === "system" ? systemAppearance : appearance;
    try {
      window.localStorage?.setItem(storageKey, appearance);
    } catch {
      // The selected theme still applies when storage is unavailable.
    }
  }, [appearance, systemAppearance]);

  const resolvedAppearance = appearance === "system" ? systemAppearance : appearance;

  return (
    <AppThemeContext.Provider value={{ appearance, setAppearance }}>
      <ThemeProvider colorMode={resolvedAppearance} dayScheme="light" nightScheme="dark">
        <BaseStyles className="app-theme">{children}</BaseStyles>
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
}

export function useAppTheme() {
  const value = useContext(AppThemeContext);
  if (!value) throw new Error("useAppTheme must be used within AppTheme");
  return value;
}
